import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/lib/seo-config";

const DEFAULT_OG_IMAGE = "https://www.drshimaaanwer.com/og-image.webp";

export interface BreadcrumbItem {
  name: string;
  url?: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  article?: {
    publishedTime: string;
    modifiedTime?: string;
    section?: string;
    author?: string;
    tags?: string[];
  };
  /** Additional JSON-LD schemas to inject (each will be a separate script tag) */
  jsonLd?: Record<string, unknown>[];
  /** Per-page hreflang map. Falls back to sensible defaults if omitted. */
  alternateUrls?: Record<string, string>;
  /** OG locale (default ar_EG; use ar_SA on KSA page). */
  ogLocale?: string;
  /** Pagination: URL of the previous page in the series. */
  prevUrl?: string;
  /** Pagination: URL of the next page in the series. */
  nextUrl?: string;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noindex = false,
  breadcrumbs,
  article,
  jsonLd,
  alternateUrls,
  ogLocale = "ar_EG",
  prevUrl,
  nextUrl,
}: SEOHeadProps) => {
  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0
    ? JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          ...(item.url ? { item: item.url } : {}),
        })),
      })
    : null;

  // Default hreflang map: EG content lives at the canonical URL,
  // SA content lives at /sa. Pages can override via alternateUrls.
  const defaultAlternates: Record<string, string> = {
    "ar-EG": canonical,
    "ar": canonical,
  };
  const alternates = alternateUrls ?? defaultAlternates;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}

      {/* hreflang — only emit tags that point to genuinely different URLs */}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      {Object.entries(alternates).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Robots */}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={ogLocale} />
      {ogLocale !== "ar_EG" && <meta property="og:locale:alternate" content="ar_EG" />}
      {ogLocale !== "ar_SA" && <meta property="og:locale:alternate" content="ar_SA" />}
      <meta property="og:site_name" content="د. شيماء أنور — استشاري التغذية العلاجية" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* BreadcrumbList JSON-LD */}
      {breadcrumbLd && (
        <script type="application/ld+json">{breadcrumbLd}</script>
      )}

      {/* Additional JSON-LD schemas */}
      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Article-specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
    </Helmet>
  );
};

export default SEOHead;
